import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Button } from '@apps/web/components/ui/button';
import { Checkbox } from '@apps/web/components/ui/checkbox';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@apps/web/components/ui/dialog';
import { Switch } from '@apps/web/components/ui/switch';
import {
    Tooltip,
    TooltipContent,
    TooltipTrigger,
} from '@apps/web/components/ui/tooltip';
import { useFormatPersonName } from '@apps/web/hooks/use_person_name_format';
import {
    type AutoAssignCaregiverPolicy,
    CLIENT_AUTO_ASSIGN_POLICY_LABELS,
    type ClientAutoAssignPolicy,
    getAutoAssignTooltip,
} from '@apps/web/lib/auto_assign';
import { formatDateAndTimeWithWeekday } from '@apps/web/lib/date_utils';
import { getAutoAssignShiftIds } from '@apps/web/lib/outreach_utils';
import { cn } from '@apps/web/lib/utils';
import { type JoinedOutreachRead } from '@libraries/typescript/api_client';

type ShiftGroup = NonNullable<JoinedOutreachRead['shift_groups']>[number];
type ShiftItem = JoinedOutreachRead['shifts'][number];

export function nextAutoAssignShiftIds(
    current: ReadonlySet<string>,
    shiftId: string,
    group: Pick<ShiftGroup, 'shift_ids' | 'group_mode'> | null,
    checked: boolean,
): Set<string> {
    const next = new Set(current);
    const ids = group?.group_mode === 'all' ? group.shift_ids : [shiftId];
    for (const id of ids) {
        if (checked) {
            next.add(id);
        } else {
            next.delete(id);
        }
    }
    return next;
}

export function autoAssignGroupForShift(
    shiftId: string,
    groups: ReadonlyArray<ShiftGroup>,
): ShiftGroup | null {
    return groups.find((group) => group.shift_ids.includes(shiftId)) ?? null;
}

export function isAllOrNoneAutoAssignGroup(
    group: Pick<ShiftGroup, 'shift_ids' | 'group_mode'> | null,
): group is Pick<ShiftGroup, 'shift_ids' | 'group_mode'> {
    return (
        group !== null &&
        group.group_mode === 'all' &&
        group.shift_ids.length > 1
    );
}

export function isAutoAssignShiftChecked(
    shiftId: string,
    draftShiftIds: ReadonlySet<string>,
    group: Pick<ShiftGroup, 'shift_ids' | 'group_mode'> | null,
): boolean {
    if (isAllOrNoneAutoAssignGroup(group)) {
        return group.shift_ids.every((id) => draftShiftIds.has(id));
    }
    return draftShiftIds.has(shiftId);
}

export type AutoAssignGroupChangeRow = {
    readonly shiftId: string;
    readonly label: string;
    readonly nextChecked: boolean;
};

export function autoAssignGroupChangeRows(
    group: Pick<ShiftGroup, 'shift_ids'>,
    current: ReadonlySet<string>,
    next: ReadonlySet<string>,
    shiftLabel: (shiftId: string) => string,
): ReadonlyArray<AutoAssignGroupChangeRow> {
    return group.shift_ids
        .filter((id) => current.has(id) !== next.has(id))
        .map((id) => ({
            shiftId: id,
            label: shiftLabel(id),
            nextChecked: next.has(id),
        }));
}

export type PendingAutoAssignGroupChange = {
    readonly group: Pick<ShiftGroup, 'shift_ids' | 'group_mode'>;
    readonly checked: boolean;
    readonly nextShiftIds: ReadonlySet<string>;
};

function savedAutoAssignKey(outreach: JoinedOutreachRead): string {
    return [...getAutoAssignShiftIds(outreach)].sort().join(',');
}

function autoAssignDraftKey(shiftIds: ReadonlySet<string>): string {
    return [...shiftIds].sort().join(',');
}

export function rollbackAutoAssignDraft(
    current: ReadonlySet<string>,
    failedRequestKey: string,
    latestSavedKey: string,
): Set<string> {
    if (autoAssignDraftKey(current) !== failedRequestKey) {
        return new Set(current);
    }
    return new Set(latestSavedKey === '' ? [] : latestSavedKey.split(','));
}

export function useOutreachAutoAssignEdits({
    outreach,
    isUpdating,
    onSave,
}: {
    readonly outreach: JoinedOutreachRead;
    readonly isUpdating: boolean;
    readonly onSave: (
        autoAssignShifts: ReadonlyArray<string>,
    ) => void | Promise<void>;
}): {
    readonly draftShiftIds: ReadonlySet<string>;
    readonly isUpdating: boolean;
    readonly pendingGroupChange: PendingAutoAssignGroupChange | null;
    readonly isShiftChecked: (shiftId: string) => boolean;
    readonly requestToggleShift: (shiftId: string, checked: boolean) => void;
    readonly confirmPendingGroupChange: () => void;
    readonly dismissPendingGroupChange: () => void;
} {
    const savedShiftIds = getAutoAssignShiftIds(outreach);
    const savedKey = savedAutoAssignKey(outreach);
    const savedKeyRef = useRef(savedKey);
    savedKeyRef.current = savedKey;
    const [draftShiftIds, setDraftShiftIds] = useState<Set<string>>(
        () => new Set(savedShiftIds),
    );
    const [pendingGroupChange, setPendingGroupChange] =
        useState<PendingAutoAssignGroupChange | null>(null);

    useEffect(() => {
        setDraftShiftIds(new Set(savedKey === '' ? [] : savedKey.split(',')));
        setPendingGroupChange(null);
    }, [savedKey]);

    const shiftGroups = useMemo(
        () => outreach.shift_groups ?? [],
        [outreach.shift_groups],
    );
    const persistShiftIds = useCallback(
        (nextShiftIds: ReadonlySet<string>) => {
            const requestKey = autoAssignDraftKey(nextShiftIds);
            setDraftShiftIds(new Set(nextShiftIds));
            void Promise.resolve(onSave([...nextShiftIds])).catch(() => {
                setDraftShiftIds((current) =>
                    rollbackAutoAssignDraft(
                        current,
                        requestKey,
                        savedKeyRef.current,
                    ),
                );
            });
        },
        [onSave],
    );

    const isShiftChecked = useCallback(
        (shiftId: string) =>
            isAutoAssignShiftChecked(
                shiftId,
                draftShiftIds,
                autoAssignGroupForShift(shiftId, shiftGroups),
            ),
        [draftShiftIds, shiftGroups],
    );

    const requestToggleShift = useCallback(
        (shiftId: string, checked: boolean) => {
            const group = autoAssignGroupForShift(shiftId, shiftGroups);
            const nextShiftIds = nextAutoAssignShiftIds(
                draftShiftIds,
                shiftId,
                group,
                checked,
            );
            const isUnchanged =
                nextShiftIds.size === draftShiftIds.size &&
                [...nextShiftIds].every((id) => draftShiftIds.has(id));
            if (isUnchanged) {
                return;
            }
            if (isAllOrNoneAutoAssignGroup(group)) {
                setPendingGroupChange({
                    group,
                    checked,
                    nextShiftIds,
                });
                return;
            }
            persistShiftIds(nextShiftIds);
        },
        [draftShiftIds, persistShiftIds, shiftGroups],
    );

    const confirmPendingGroupChange = useCallback(() => {
        if (pendingGroupChange === null) {
            return;
        }
        const nextShiftIds = pendingGroupChange.nextShiftIds;
        setPendingGroupChange(null);
        persistShiftIds(nextShiftIds);
    }, [pendingGroupChange, persistShiftIds]);

    const dismissPendingGroupChange = useCallback(() => {
        setPendingGroupChange(null);
    }, []);

    return {
        draftShiftIds,
        isUpdating,
        pendingGroupChange,
        isShiftChecked,
        requestToggleShift,
        confirmPendingGroupChange,
        dismissPendingGroupChange,
    };
}

export function AutoAssignShiftHeader({
    policy,
}: {
    readonly policy: AutoAssignCaregiverPolicy | undefined;
}) {
    return (
        <div
            className="flex flex-col items-center"
            data-testid="auto-assign-first-responder-toggle-trigger"
        >
            <Tooltip>
                <TooltipTrigger asChild={true}>
                    <span className="cursor-default whitespace-nowrap">
                        Auto-assign
                    </span>
                </TooltipTrigger>
                <TooltipContent className="max-w-xs">
                    {getAutoAssignTooltip(policy)}
                </TooltipContent>
            </Tooltip>
        </div>
    );
}

export function AutoAssignShiftCell({
    shiftId,
    patientName,
    checked,
    disabled,
    onCheckedChange,
}: {
    readonly shiftId: string;
    readonly patientName: string;
    readonly checked: boolean;
    readonly disabled: boolean;
    readonly onCheckedChange: (checked: boolean) => void;
}) {
    return (
        <Switch
            checked={checked}
            disabled={disabled}
            aria-label={`Auto-assign ${patientName}`}
            data-testid={`auto-assign-shift-${shiftId}`}
            onCheckedChange={onCheckedChange}
        />
    );
}

export type ClientAutoAssignPolicyOverride = {
    readonly clientId: string;
    readonly clientName: string;
    readonly policy: ClientAutoAssignPolicy;
};

/**
 * Lists the outreach clients whose saved auto-assign policy differs from the
 * organization default. Shared by the default detail section and the compact
 * (v3) shift table so both layouts explain the same per-shift decision.
 */
export function AutoAssignClientPolicyOverrides({
    overrides,
    className,
}: {
    readonly overrides: ReadonlyArray<ClientAutoAssignPolicyOverride>;
    readonly className?: string;
}) {
    if (overrides.length === 0) {
        return null;
    }
    return (
        <div
            className={cn(
                'text-muted-foreground space-y-0.5 text-xs',
                className,
            )}
            data-testid="auto-assign-client-policy-override"
        >
            {overrides.map((override) => (
                <div key={override.clientId}>
                    {override.clientName}:{' '}
                    {CLIENT_AUTO_ASSIGN_POLICY_LABELS[override.policy]} (client
                    setting, differs from organization default)
                </div>
            ))}
        </div>
    );
}

export function AutoAssignDetailSection({
    outreach,
    timezone,
    isUpdating,
    onSave,
    clientPolicyOverrides = [],
}: {
    readonly outreach: JoinedOutreachRead;
    readonly timezone: string;
    readonly isUpdating: boolean;
    readonly onSave: (autoAssignShifts: ReadonlyArray<string>) => void;
    readonly clientPolicyOverrides?: ReadonlyArray<ClientAutoAssignPolicyOverride>;
}) {
    const { formatName } = useFormatPersonName();
    const {
        draftShiftIds,
        isShiftChecked,
        pendingGroupChange,
        requestToggleShift,
        confirmPendingGroupChange,
        dismissPendingGroupChange,
    } = useOutreachAutoAssignEdits({
        outreach,
        isUpdating,
        onSave,
    });

    const shiftGroups = useMemo(
        () => outreach.shift_groups ?? [],
        [outreach.shift_groups],
    );
    const groupedShiftIds = useMemo(() => {
        const ids = new Set<string>();
        for (const group of shiftGroups) {
            for (const shiftId of group.shift_ids) {
                ids.add(shiftId);
            }
        }
        return ids;
    }, [shiftGroups]);

    const ungroupedShifts = useMemo(
        () => outreach.shifts.filter((shift) => !groupedShiftIds.has(shift.id)),
        [outreach.shifts, groupedShiftIds],
    );

    const shiftLabel = useCallback(
        (shift: ShiftItem) => {
            const client = formatName(shift.patient_name) || 'Unknown client';
            const start = shift.shift_start_time
                ? formatDateAndTimeWithWeekday(shift.shift_start_time, timezone)
                : '';
            return `${client}${start ? ` \u2013 ${start}` : ''}`;
        },
        [timezone, formatName],
    );

    const shiftsById = useMemo(() => {
        const map = new Map<string, ShiftItem>();
        for (const shift of outreach.shifts) {
            map.set(shift.id, shift);
        }
        return map;
    }, [outreach.shifts]);

    const renderShiftCheckbox = (shift: ShiftItem) => (
        <label key={shift.id} className="flex items-center gap-2 text-sm">
            <Checkbox
                checked={isShiftChecked(shift.id)}
                disabled={isUpdating}
                onCheckedChange={(checked) =>
                    requestToggleShift(shift.id, checked === true)
                }
            />
            <span>{shiftLabel(shift)}</span>
        </label>
    );

    return (
        <div data-testid="auto-assign-first-responder-toggle-trigger">
            <AutoAssignClientPolicyOverrides
                overrides={clientPolicyOverrides}
                className="mb-2"
            />
            <div className="space-y-2">
                <div className="space-y-2 pl-1">
                    {shiftGroups.map((group, index) => (
                        <div key={group.id} className="space-y-1">
                            <span className="text-muted-foreground text-xs font-medium">
                                Group {index + 1} (
                                {group.group_mode === 'all'
                                    ? 'All or none'
                                    : 'Any'}
                                )
                            </span>
                            {group.shift_ids.map((shiftId) => {
                                const shift = shiftsById.get(shiftId);
                                return shift
                                    ? renderShiftCheckbox(shift)
                                    : null;
                            })}
                        </div>
                    ))}
                    {ungroupedShifts.length > 0 && shiftGroups.length > 0 && (
                        <span className="text-muted-foreground text-xs font-medium">
                            Single Shifts
                        </span>
                    )}
                    {ungroupedShifts.map((shift) => renderShiftCheckbox(shift))}
                </div>
            </div>
            <AutoAssignGroupChangeDialog
                pending={pendingGroupChange}
                currentShiftIds={draftShiftIds}
                shifts={outreach.shifts}
                timezone={timezone}
                onConfirm={confirmPendingGroupChange}
                onDismiss={dismissPendingGroupChange}
            />
        </div>
    );
}

function autoAssignShiftLabel(
    shift: ShiftItem | undefined,
    timezone: string,
    fallbackId: string,
    formatName: (name: string | null | undefined) => string,
): string {
    if (!shift) {
        return `Shift ${fallbackId}`;
    }
    const client = formatName(shift.patient_name) || 'Unknown client';
    const start = shift.shift_start_time
        ? formatDateAndTimeWithWeekday(shift.shift_start_time, timezone)
        : '';
    return start ? `${client} \u2013 ${start}` : client;
}

export function AutoAssignGroupChangeDialog({
    pending,
    currentShiftIds,
    shifts,
    timezone,
    onConfirm,
    onDismiss,
}: {
    readonly pending: PendingAutoAssignGroupChange | null;
    readonly currentShiftIds: ReadonlySet<string>;
    readonly shifts: ReadonlyArray<ShiftItem>;
    readonly timezone: string;
    readonly onConfirm: () => void;
    readonly onDismiss: () => void;
}) {
    const { formatName } = useFormatPersonName();
    const shiftsById = useMemo(() => {
        const map = new Map<string, ShiftItem>();
        for (const shift of shifts) {
            map.set(shift.id, shift);
        }
        return map;
    }, [shifts]);

    const rows = useMemo(() => {
        if (pending === null) {
            return [];
        }
        return autoAssignGroupChangeRows(
            pending.group,
            currentShiftIds,
            pending.nextShiftIds,
            (shiftId) =>
                autoAssignShiftLabel(
                    shiftsById.get(shiftId),
                    timezone,
                    shiftId,
                    formatName,
                ),
        );
    }, [currentShiftIds, pending, shiftsById, timezone, formatName]);

    return (
        <Dialog
            open={pending !== null}
            onOpenChange={(isOpen) => {
                if (!isOpen) {
                    onDismiss();
                }
            }}
        >
            <DialogContent className="max-w-md">
                <DialogHeader>
                    <DialogTitle>
                        Auto-assign must match for this group
                    </DialogTitle>
                    <DialogDescription>
                        {
                            'These shifts are in an all-or-none group, so they all get the same auto-assign setting. You can\u2019t turn it on for some and off for others.'
                        }
                    </DialogDescription>
                </DialogHeader>
                {rows.length > 0 && (
                    <div className="space-y-2">
                        <div className="text-sm font-medium">
                            These shifts will change to{' '}
                            {pending?.checked ? 'on' : 'off'}:
                        </div>
                        <ul className="list-disc space-y-1 pl-5 text-sm">
                            {rows.map((row) => (
                                <li key={row.shiftId}>{row.label}</li>
                            ))}
                        </ul>
                    </div>
                )}
                <DialogFooter>
                    <Button variant="outline" onClick={onDismiss}>
                        Cancel
                    </Button>
                    <Button onClick={onConfirm}>Apply</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
